import { Erro } from "@/repositorios/erros"
import { reactive } from "vue"

type FuncaoResultante<R> = (resultado: R) => void
type Resolver<R> = {
  erro?: FuncaoResultante<EscritorErro>
  sucesso: FuncaoResultante<R>
}
type Status = "sucesso" | "erro" | "inicial" | "pendente" | "vazio"

interface EscritorInicial {
  status: "inicial"
}
export interface EscritorErro {
  status: "erro"
  mensagem: string
  mensagemParaQuem: "usuario" | "dev"
  codigo?: number | "cancelado"
}
interface EscritorVazio {
  status: "vazio"
}
interface EscritorPendente {
  status: "pendente"
}
interface EscritorSucesso<T = any> {
  status: "sucesso"
  dados: T
}

export type EscritorEstados<T = any> =
  | EscritorInicial
  | EscritorErro
  | EscritorPendente
  | EscritorVazio
  | EscritorSucesso<T>

type Argumentos<I = undefined, C = any> = [id?: I, dado?: C]
type Repositorio<R, I, C> = (...argumentos: Argumentos<I, C>) => Promise<R>

export interface Escritor<R = any, I = undefined, C = any> {
  resultado: EscritorEstados<R>
  repositorio: Repositorio<R, I, C>
  argumentos: Argumentos<I, C>
  readonly mudarEstado: (novoResultado: EscritorEstados<R>) => void
  readonly mudarArgumentos: (novoArgumentos: Argumentos<I, C>) => void
  readonly executar: () => Promise<void>
}

export function usarEscritorDeDados() {
  const escritor = <R, I = undefined, C = any>(
    repositorio: Repositorio<R, I, C>,
    argumentos: Argumentos<I, C> = []
  ) => {
    return reactive<Escritor<R, I, C>>({
      resultado: {
        status: "inicial"
      },
      argumentos: argumentos,
      repositorio: repositorio,
      mudarEstado(novoResultado: EscritorEstados<R>) {
        this.resultado = novoResultado
      },
      mudarArgumentos(novoArgumentos: Argumentos<I, C>) {
        this.argumentos = novoArgumentos
      },
      async executar() {
        try {
          this.mudarEstado({
            status: "pendente",
          })

          const resultado = await this.repositorio(...this.argumentos)

          this.mudarEstado({
            status: "sucesso",
            dados: resultado
          })
          Promise.resolve()
        } catch (error) {
          const erro = error as Erro
          this.mudarEstado({
            status: "erro",
            mensagem: erro.detalhe,
            mensagemParaQuem: "usuario",
            codigo: erro.codigo,
          })
          Promise.resolve()
        }
      },
    })
  }

  const resolver = <R>(resultado: EscritorEstados<R>, { erro, sucesso }: Resolver<R>) => {
    if (resultado.status === "sucesso") {
      sucesso(resultado.dados)
    }
    if (resultado.status === "erro" && erro) {
      erro(resultado)
    }
  }

  return {
    escritor,
    resolver,
  }
}
